import { BleClient, ScanMode } from '@capacitor-community/bluetooth-le'
import { Capacitor } from '@capacitor/core'
import { logger, LogLevel } from '@/services/logger'

export interface BleDevice {
  deviceId: string
  name?: string
  rssi?: number
  uuids?: string[]
}

// Target service UUID to search for
const TARGET_SERVICE_UUID = '1a6541e5-c692-4558-9554-539760c1f97f'

/**
 * Scan Modes Explanation:
 * - SCAN_MODE_LOW_LATENCY: Fastest, most power-intensive, best for quick detection
 *   Use when: You need immediate response (tap-to-connect scenarios)
 *   Cons: Can miss devices with longer advertisement intervals
 *
 * - SCAN_MODE_BALANCED: Balanced between power and performance
 *   Use when: Background monitoring or periodic checks (recommended for most cases)
 *   Cons: Slightly slower initial detection
 *
 * - SCAN_MODE_LOW_POWER: Slowest, most power-efficient
 *   Use when: Long-term monitoring with minimal battery impact
 *   Cons: Can take several seconds to detect devices
 */

export class BleService {
  private scanning = false

  async initialize(): Promise<void> {
    console.log('[BleService] Initializing BLE...')
    console.log('[BleService] Platform:', Capacitor.getPlatform())

    const platform = Capacitor.getPlatform()
    if (platform === 'web') {
      const error = new Error('BLE scanning is not supported on web platform. Please use a native Android or iOS device.')
      await logger.logAdmin(
        LogLevel.ERROR,
        'ble',
        'BLE initialization failed: Web platform not supported',
        { platform, error: error.message }
      )
      throw error
    }

    try {
      await BleClient.initialize()
      console.log('[BleService] BLE initialized successfully')
      await logger.logAdmin(
        LogLevel.INFO,
        'ble',
        'BLE service initialized successfully',
        { platform }
      )
    } catch (error) {
      console.error('[BleService] Initialization failed:', error)
      await logger.logAdmin(
        LogLevel.ERROR,
        'ble',
        'BLE initialization failed',
        {
          platform,
          error: error instanceof Error ? error.message : 'Unknown error'
        },
        error instanceof Error ? error : undefined
      )
      throw error
    }
  }

  async startScanning(
    onDeviceFound: (device: BleDevice) => void,
    onError?: (error: Error) => void,
    options?: {
      allowDuplicates?: boolean
      scanMode?: ScanMode
    }
  ): Promise<void> {
    if (this.scanning) {
      console.warn('[BleService] Already scanning, ignoring start request')
      await logger.logAdmin(
        LogLevel.WARN,
        'ble',
        'BLE scan already in progress, ignoring start request',
        { alreadyScanning: true }
      )
      return
    }

    const scanOptions = {
      allowDuplicates: options?.allowDuplicates ?? true,
      scanMode: options?.scanMode ?? ScanMode.SCAN_MODE_LOW_LATENCY
    }

    console.log('[BleService] Starting BLE scan...')
    console.log('[BleService] Looking for service UUID:', TARGET_SERVICE_UUID)
    console.log('[BleService] Scan options:', scanOptions)

    await logger.logAdmin(
      LogLevel.INFO,
      'ble',
      'Starting BLE scan',
      {
        targetServiceUUID: TARGET_SERVICE_UUID,
        scanOptions
      }
    )

    try {
      await this.initialize()

      // Native platform only: Use requestLEScan with service UUID filter
      await BleClient.requestLEScan(
        {
          services: [TARGET_SERVICE_UUID],
          scanMode: scanOptions.scanMode,
          allowDuplicates: scanOptions.allowDuplicates,
        },
        (result) => {
          console.log('[BleService] Device found, verifying UUID...', result)

          // Explicitly verify the target UUID is present in the discovered device
          // This prevents false positives from other Bluetooth devices or iOS BLE caching issues
          if (!result.uuids || !result.uuids.includes(TARGET_SERVICE_UUID)) {
            console.warn('[BleService] Device found but missing target UUID, ignoring...', {
              deviceId: result.device.deviceId,
              deviceName: result.device.name,
              foundUUIDs: result.uuids,
              expectedUUID: TARGET_SERVICE_UUID
            })

            logger.logAdmin(
              LogLevel.WARN,
              'ble',
              'BLE device rejected - UUID mismatch',
              {
                deviceId: result.device.deviceId,
                deviceName: result.device.name,
                foundUUIDs: result.uuids,
                expectedUUID: TARGET_SERVICE_UUID
              }
            ).catch(err => console.error('[BleService] Failed to log UUID mismatch:', err))

            return // Skip this device
          }

          console.log('[BleService] Device verified with correct target UUID:', result)

          const device: BleDevice = {
            deviceId: result.device.deviceId,
            name: result.device.name,
            rssi: result.rssi,
            uuids: result.uuids
          }

          // Log device discovery
          logger.logAdmin(
            LogLevel.INFO,
            'ble',
            `BLE device discovered: ${device.name || device.deviceId}`,
            {
              deviceId: device.deviceId,
              deviceName: device.name,
              rssi: device.rssi,
              uuids: device.uuids,
              verified: true
            }
          ).catch(err => console.error('[BleService] Failed to log device discovery:', err))

          onDeviceFound(device)
        }
      )

      this.scanning = true
      console.log('[BleService] BLE scan started successfully (native platform)')

      await logger.logAdmin(
        LogLevel.INFO,
        'ble',
        'BLE scan started successfully',
        {
          platform: Capacitor.getPlatform(),
          scanOptions
        }
      )
    } catch (error) {
      console.error('[BleService] Failed to start scan:', error)
      this.scanning = false

      await logger.logAdmin(
        LogLevel.ERROR,
        'ble',
        'Failed to start BLE scan',
        {
          error: error instanceof Error ? error.message : 'Unknown error',
          targetServiceUUID: TARGET_SERVICE_UUID
        },
        error instanceof Error ? error : undefined
      )

      if (onError) {
        onError(error instanceof Error ? error : new Error('Failed to start BLE scan'))
      }

      throw error
    }
  }

  async stopScanning(): Promise<void> {
    console.log('[BleService] Stopping BLE scan...')

    try {
      if (this.scanning) {
        await BleClient.stopLEScan()
        this.scanning = false
        console.log('[BleService] BLE scan stopped')

        await logger.logAdmin(
          LogLevel.INFO,
          'ble',
          'BLE scan stopped successfully',
          { wasScanning: true }
        )
      } else {
        await logger.logAdmin(
          LogLevel.DEBUG,
          'ble',
          'BLE scan stop requested but scan was not active',
          { wasScanning: false }
        )
      }
    } catch (error) {
      console.error('[BleService] Error stopping scan:', error)
      this.scanning = false

      await logger.logAdmin(
        LogLevel.ERROR,
        'ble',
        'Error stopping BLE scan',
        { error: error instanceof Error ? error.message : 'Unknown error' },
        error instanceof Error ? error : undefined
      )
    }
  }

  isScanning(): boolean {
    return this.scanning
  }
}
